import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fmt } from "@/components/engines/shared";

const EASE = [0.16, 1, 0.3, 1];

const ago = (iso) => {
  const s = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

export const LiveActivity = ({ activity }) => {
  const [tick, setTick] = useState(0);
  const recent = activity?.recent || [];
  const showCounts = (activity?.documents_total || 0) >= 10;

  useEffect(() => {
    if (recent.length < 2) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 4500);
    return () => clearInterval(id);
  }, [recent.length]);

  if (!activity || (!showCounts && !recent.length)) return null;

  const run = recent.length ? recent[tick % recent.length] : null;

  return (
    <div className="border-b border-hairline bg-white" data-testid="live-activity">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-3.5 flex items-center justify-between gap-6 flex-wrap">
        {showCounts ? (
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-fog" data-testid="activity-counts">
            <span className="text-ink tabular-nums">{activity.documents_total.toLocaleString()}</span> documents run
            {activity.bytes_saved_week > 0 && (
              <> · <span className="text-ink tabular-nums">{fmt(activity.bytes_saved_week)}</span> saved this week</>
            )}
            {" "}· <span className="text-azure">0 files stored</span>
          </p>
        ) : (
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-fog" data-testid="activity-counts">
            Every run is real · <span className="text-azure">0 files stored</span>
          </p>
        )}
        {run && (
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-mist min-h-[1rem]" data-testid="activity-ticker" aria-live="polite">
            <AnimatePresence mode="wait">
              <motion.span
                key={tick}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="inline-block"
              >
                Latest — {run.engine}
                {run.after_bytes ? ` · ${fmt(run.before_bytes)} → ${fmt(run.after_bytes)}` : run.before_bytes ? ` · ${fmt(run.before_bytes)}` : ""}
                {" "}· {ago(run.at)}
              </motion.span>
            </AnimatePresence>
          </p>
        )}
      </div>
    </div>
  );
};
